import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import { useDispatch, useSelector } from 'react-redux';
import { customization, dialogs, i18next } from '@yola/ws-sdk';
import { converters, HSLColor } from '@yola/yola-palette-generator';
import dialogTypes from '../../dialogs/constants/dialog-types';
import segment from '../../analytics/segment';
import helpers from '../helpers';
import actions from '../actions';
import triggerIds from '../constants/trigger-ids';
import dialogCallSourceIds from '../constants/dialog-call-source-ids';
import useInjectPaletteToTemplate from '../hooks/use-inject-palette-to-template';
import BaseColorsPickerDialog from '../components/base-colors-picker-dialog';
import { ACCENT_COLOR_TYPE, DARK_COLOR_TYPE } from '../constants/color-types';

const { hsl2hex } = converters;

const {
  colorValidation,
  generateCustomPaletteId,
  getAdjustedBaseColors,
  generatePalette,
  mapColorKeyToColorType,
  setMultipleBaseColors,
  createHslColorString,
  parseHslColorString,
} = helpers;

const {
  constants: { events },
  trackers: { trackEvent },
} = segment;

const getCaptions = (cameFromAddButton, colorType) => {
  const lightWarning = i18next.t(
    'We recommend using a lighter shade to increase the legibility on your website.'
  );
  const darkWarning = i18next.t(
    'We recommend using a darker shade to increase the legibility on your website.'
  );

  const warning = colorType === DARK_COLOR_TYPE ? darkWarning : lightWarning;

  return {
    submitButton: i18next.t('Save'),
    cancelButton: i18next.t('Cancel'),
    breadcrumbs: [i18next.t('Design'), i18next.t('Colors')],
    title: cameFromAddButton ? i18next.t('New palette') : i18next.t('Edit palette'),
    description: i18next.t(
      'Select favorite colors for the palette to generate color schemes for the blocks on your website'
    ),
    tooltips: {
      primary: i18next.t(
        'This color is used for primary button, active navigation items, and graphic elements'
      ),
      secondary: i18next.t(
        'This color is used for links, feature and social icons, and other design elements'
      ),
    },
    presetsSubtitle: i18next.t('Presets'),
    warning,
  };
};

const BaseColorsPickerContainer = (props) => {
  const { getSharedData, onDialogMainAction, onDialogCancel, resolveSharedData, triggerId } = props;

  const { offsetX, offsetY, originPalette, editPalette, customColorPalettes } = getSharedData();

  const dispatch = useDispatch();
  const colorPaletteConfig = useSelector(customization.selectors.getColorPaletteConfig);
  const defaultColorPalettes = useSelector(customization.selectors.getColorPalettes);

  const initialColorPalette = editPalette || originPalette;
  const [colorItems, setColorItems] = useState(initialColorPalette.colors);
  const [dialogPosition, updatePosition] = useState({ offsetX, offsetY });
  const [selectedColorKey, setSelectedColorKey] = useState('ws-light-0');
  const [isWarningShown, setWarningVisibility] = useState(false);
  const workerRef = useRef(null);

  const cameFromAddButton = triggerId === triggerIds.ADD_CUSTOM_PALETTE;
  const colorType = mapColorKeyToColorType(selectedColorKey);
  const captions = getCaptions(cameFromAddButton, colorType);

  const injectPaletteToTemplate = useInjectPaletteToTemplate();
  const changeCustomColors = useCallback(
    (palette) => dispatch(actions.changeColor(palette)),
    [dispatch]
  );

  const handleInjectPaletteToTemplate = useCallback(
    (compiledPalette) => {
      injectPaletteToTemplate(compiledPalette);
      changeCustomColors(compiledPalette);
    },
    [injectPaletteToTemplate, changeCustomColors]
  );

  const updateSharedData = (customData) => {
    resolveSharedData({
      offsetX: dialogPosition.offsetX,
      offsetY: dialogPosition.offsetY,
      ...customData,
    });
  };

  const getEditPalette = (editPaletteToUpdate) => {
    const palette = { ...editPaletteToUpdate };
    const shouldCreatePalette = !palette.parentId;

    if (shouldCreatePalette) {
      const palettesIds = customColorPalettes.map(({ id }) => id);

      palette.parentId = editPaletteToUpdate.id;
      palette.id = generateCustomPaletteId(palettesIds);
    }

    return palette;
  };

  const addColorPalette = (newPalette) => [...customColorPalettes, newPalette];

  const updateColorPalette = (paletteToUpdate) => {
    const filteredState = customColorPalettes.filter(({ id }) => id !== paletteToUpdate.id);
    return [...filteredState, paletteToUpdate];
  };

  const onClose = () => {
    if (cameFromAddButton) {
      dispatch(
        dialogs.actions.show(dialogTypes.DESIGN_SETTINGS_COLORS_DIALOG, {
          sourceId: dialogCallSourceIds.BACK_FROM_COLOR_PALETTE_SETTINGS,
        })
      );
    } else {
      dispatch(
        dialogs.actions.show(dialogTypes.DESIGN_SETTINGS_CUSTOM_PALETTE, {
          triggerId,
          colorPreset: initialColorPalette.id,
        })
      );
    }
  };

  const handleSubmit = () => {
    const compiledPalette = generatePalette(
      setMultipleBaseColors({ colorsToUpdate: colorItems, colorPalette: initialColorPalette }),
      colorPaletteConfig
    );
    handleInjectPaletteToTemplate(compiledPalette);
    const currentEditPalette = getEditPalette(compiledPalette);

    const updatedPalettes =
      currentEditPalette.id !== initialColorPalette.id
        ? addColorPalette(currentEditPalette)
        : updateColorPalette(currentEditPalette);

    updateSharedData({
      activeColorPaletteId: currentEditPalette.id,
      updatedPalettes,
      editPalette: cameFromAddButton ? null : currentEditPalette,
    });

    onDialogMainAction({
      triggerId,
      adjustedColors: getAdjustedBaseColors(originPalette, currentEditPalette),
      colorPreset: originPalette.id,
    });

    onClose();
  };

  const handleCancel = () => {
    const cancelDialog = () => {
      onDialogCancel({
        triggerId,
      });
      onClose();
    };

    const isBaseColorsChanged = colorItems !== initialColorPalette.colors;

    if (editPalette && isBaseColorsChanged) {
      const revertedPalette = generatePalette(
        setMultipleBaseColors({
          colorsToUpdate: editPalette.colors,
          colorPalette: initialColorPalette,
        }),
        colorPaletteConfig
      );

      handleInjectPaletteToTemplate(revertedPalette);
      updateSharedData({ editPalette: revertedPalette });
      cancelDialog();
      return;
    }

    if (editPalette && !isBaseColorsChanged) {
      cancelDialog();
      return;
    }

    if (!editPalette && isBaseColorsChanged) {
      const revertedPalette = generatePalette(
        setMultipleBaseColors({
          colorsToUpdate: originPalette.colors,
          colorPalette: initialColorPalette,
        }),
        colorPaletteConfig
      );

      handleInjectPaletteToTemplate(revertedPalette);
      cancelDialog();
      return;
    }
    cancelDialog();
  };

  const handleColorChange = ({ hsl }) => {
    setColorItems((prev) => ({
      ...prev,
      [selectedColorKey]: createHslColorString(hsl),
    }));
  };

  const handlePresetSelect = (preset) => {
    setColorItems(preset.colors);

    trackEvent(events.DESIGN_SETTINGS_COLOR_PRESET_CHANGED, {
      dialogId: dialogTypes.BASE_COLORS_PICKER_DIALOG,
      colorPreset: preset.id,
    });
  };

  const handleDragEnd = (_, { x, y }) => {
    updatePosition({
      offsetX: x,
      offsetY: y,
    });
  };

  const handleActiveColorSelect = (colorKey) => {
    setSelectedColorKey(colorKey);

    const activeColorType = mapColorKeyToColorType(colorKey);

    if (activeColorType === ACCENT_COLOR_TYPE) {
      setWarningVisibility(false);
      return;
    }

    const { h, s, l } = parseHslColorString(colorItems[colorKey]);
    const hslColor = new HSLColor(h, s, l);

    setWarningVisibility(!colorValidation(hsl2hex(hslColor), activeColorType));
  };

  const handleColorChangeComplete = ({ hex }) => {
    if (colorType !== ACCENT_COLOR_TYPE) {
      setWarningVisibility(!colorValidation(hex, colorType));
    }
  };

  useEffect(() => {
    const workerListener = ({ data }) => {
      handleInjectPaletteToTemplate(data.palette);
    };

    workerRef.current = new Worker(
      new URL('../workers/generate-color-palette.worker.js', import.meta.url)
    );
    workerRef.current.addEventListener('message', workerListener);
    return () => {
      workerRef.current.removeEventListener('message', workerListener);
      workerRef.current.terminate();
    };
  }, [handleInjectPaletteToTemplate, workerRef]);

  useEffect(() => {
    if (!bowser.mobile && workerRef.current) {
      const updatedPalette = setMultipleBaseColors({
        colorsToUpdate: colorItems,
        colorPalette: initialColorPalette,
      });

      workerRef.current.postMessage({
        config: colorPaletteConfig,
        ...updatedPalette,
      });
    }
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [colorItems]);

  return (
    <BaseColorsPickerDialog
      colors={colorItems}
      selectedColor={selectedColorKey}
      presets={defaultColorPalettes}
      captions={captions}
      displayWarning={isWarningShown}
      offsetX={dialogPosition.offsetX}
      offsetY={dialogPosition.offsetY}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      onDragEnd={handleDragEnd}
      onActiveColorSelect={handleActiveColorSelect}
      onColorChange={handleColorChange}
      onColorChangeComplete={handleColorChangeComplete}
      onPresetSelect={handlePresetSelect}
    />
  );
};

BaseColorsPickerContainer.propTypes = {
  getSharedData: PropTypes.func.isRequired,
  triggerId: PropTypes.string.isRequired,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
  resolveSharedData: PropTypes.func.isRequired,
};

export default BaseColorsPickerContainer;
